import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Undo as UndoIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

interface Point {
  x: number;
  y: number;
}

interface SignatureProps {
  onSignatureChange: (signature: string | null) => void;
  width?: number;
  height?: number;
  penColor?: string;
  penWidth?: number;
  backgroundColor?: string;
}

const DigitalSignature: React.FC<SignatureProps> = ({
  onSignatureChange,
  width = 400,
  height = 200,
  penColor = '#000000',
  penWidth = 2,
  backgroundColor = '#ffffff',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState<Point | null>(null);
  const [paths, setPaths] = useState<Point[][]>([]);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
  }, [penColor, penWidth, backgroundColor, width, height]);

  const getPointFromEvent = (event: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in event) {
      const touch = event.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    }
  };

  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    const point = getPointFromEvent(event);
    setIsDrawing(true);
    setLastPoint(point);
    setCurrentPath([point]);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !lastPoint) return;
    
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const currentPoint = getPointFromEvent(event);
    
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(currentPoint.x, currentPoint.y);
    ctx.stroke();

    setLastPoint(currentPoint);
    setCurrentPath(prev => [...prev, currentPoint]);
    setIsEmpty(false);
  };

  const stopDrawing = () => {
    if (isDrawing && currentPath.length > 0) {
      setPaths(prev => [...prev, currentPath]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
    setLastPoint(null);
    
    // Generate signature data URL
    const canvas = canvasRef.current;
    if (canvas && !isEmpty) {
      const dataURL = canvas.toDataURL('image/png');
      onSignatureChange(dataURL);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    
    setPaths([]);
    setCurrentPath([]);
    setIsEmpty(true);
    onSignatureChange(null);
  };

  const undoLastPath = () => {
    if (paths.length === 0) return;

    const newPaths = [...paths];
    newPaths.pop();
    setPaths(newPaths);

    // Redraw canvas
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;

    newPaths.forEach(path => {
      if (path.length > 1) {
        ctx.beginPath();
        ctx.moveTo(path[0].x, path[0].y);
        // eslint-disable-next-line no-plusplus
        for (let i = 1; i < path.length; i++) {
          ctx.lineTo(path[i].x, path[i].y);
        }
        ctx.stroke();
      }
    });

    if (newPaths.length === 0) {
      setIsEmpty(true);
      onSignatureChange(null);
    } else {
      const dataURL = canvas.toDataURL('image/png');
      onSignatureChange(dataURL);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Typography variant="h6" color="primary">
        Digital Signature
      </Typography>
      
      <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            cursor: 'crosshair',
            touchAction: 'none',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Clear signature">
          <IconButton 
            onClick={clearSignature}
            color="error"
            disabled={isEmpty}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Undo last stroke">
          <IconButton 
            onClick={undoLastPath}
            color="warning"
            disabled={paths.length === 0}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!isEmpty && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
          <CheckIcon fontSize="small" />
          <Typography variant="body2">Signature captured</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DigitalSignature;